import React, { useState, useRef } from 'react';
import { sapApi } from '../services/api';
import '../styles/Panel.css';

function PullTBPanel({ entity, onComplete }) {
  const [startDate, setStartDate] = useState('2024-04-01');
  const [endDate, setEndDate] = useState('2025-03-31');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const fileInputRef = useRef(null);

  // Check if entity has no system access (connection_type: "N/A")
  const isManualUploadEntity = entity && entity.connection_type && entity.connection_type.toUpperCase() === 'N/A';
  const connectionType = (entity?.connection_type || '').toLowerCase();
  const isXeroEntity = connectionType === 'xero';
  const isExactEntity = connectionType === 'exact_online';
  const oauthProviderLabel = isExactEntity ? 'Exact Online' : isXeroEntity ? 'Xero' : 'OAuth provider';

  const buildPullOptions = () => {
    const trimmedRedirectUrl = redirectUrl.trim();
    if (!trimmedRedirectUrl) {
      return {};
    }
    if (isExactEntity) {
      return { exactRedirectUrl: trimmedRedirectUrl };
    }
    if (isXeroEntity) {
      return { xeroRedirectUrl: trimmedRedirectUrl };
    }
    return {};
  };

  const handlePullTB = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await sapApi.pullTrialBalance(
        entity.name,
        startDate,
        endDate,
        true,
        buildPullOptions()
      );
      setAuthRequired(false);
      setAuthUrl('');
      setRedirectUrl('');
      setResult(data);
      onComplete();
    } catch (err) {
      if (err?.isApiError && err?.data?.auth_required) {
        setAuthRequired(true);
        setAuthUrl(err.data.auth_url || '');
        setError(err.data.message || err.message);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await sapApi.downloadTBTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tb_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (!isValid) {
        setError('Please select an Excel (.xlsx, .xls) or CSV file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUploadTB = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await sapApi.uploadTrialBalance(entity.name, startDate, endDate, selectedFile);
      setResult(data);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onComplete();
    } catch (err) {
      if (err.data && err.data.total_amount_sum !== undefined) {
        setError(`${err.message} (Current total: ${Number(err.data.total_amount_sum).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderUploadSection = () => (
    <>
      <div className="form-group">
        <label>Download Template:</label>
        <button
          className="btn btn-secondary"
          onClick={handleDownloadTemplate}
          disabled={loading}
          style={{ marginBottom: '10px' }}
        >
          Download TB Template
        </button>
        <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
          Download the Excel template, fill in your trial balance data, and upload it below.
        </p>
      </div>

      <div className="form-group">
        <label>Upload Trial Balance File:</label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          disabled={loading}
          style={{ marginBottom: '10px' }}
        />
        {selectedFile && (
          <p style={{ fontSize: '0.9em', color: '#28a745', marginTop: '5px' }}>
            Selected: {selectedFile.name}
          </p>
        )}
        <p style={{ fontSize: '0.85em', color: '#999', marginTop: '5px' }}>
          Accepts Excel (.xlsx, .xls) or CSV files
        </p>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleUploadTB}
        disabled={loading || !startDate || !endDate || !selectedFile}
      >
        {loading ? 'Uploading...' : 'Upload Trial Balance'}
      </button>
    </>
  );

  return (
    <div className="panel pull-tb-panel">
      <h2>1. {isManualUploadEntity ? 'Upload Trial Balance (Manual)' : 'Pull Trial Balance from SAP'}</h2>

      {isManualUploadEntity && (
        <div className="alert alert-info" style={{ marginBottom: '20px' }}>
          <strong>No System Access Available</strong>
          <p>This entity does not have SAP system access yet. Please manually upload the trial balance using the template provided.</p>
        </div>
      )}

      <div className="form-group">
        <label>Start Date:</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label>End Date:</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          disabled={loading}
        />
      </div>

      {isManualUploadEntity ? (
        /* Manual-only entities: show upload section directly */
        renderUploadSection()
      ) : (
        /* SAP entities: show Pull button + toggle for upload */
        <>
          <button
            className="btn btn-primary"
            onClick={handlePullTB}
            disabled={loading || !startDate || !endDate}
          >
            {loading && !selectedFile ? 'Pulling from SAP...' : 'Pull Trial Balance'}
          </button>

          <div style={{ marginTop: '20px', borderTop: '1px solid #e0e0e0', paddingTop: '15px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setShowUploadSection(!showUploadSection)}
              style={{ marginBottom: '10px' }}
            >
              {showUploadSection ? 'Hide Manual Upload' : 'Or Upload TB via Template'}
            </button>

            {showUploadSection && (
              <div style={{ marginTop: '10px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                {renderUploadSection()}
              </div>
            )}
          </div>
        </>
      )}

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {authRequired && (
        <div className="alert alert-info" style={{ marginTop: '15px' }}>
          <h3 style={{ marginTop: 0 }}>{oauthProviderLabel} authorization required</h3>
          <p style={{ marginBottom: '10px' }}>
            Complete authorization in your browser, then paste the full callback URL below and retry the pull.
          </p>
          {authUrl && (
            <p style={{ marginBottom: '10px' }}>
              <a href={authUrl} target="_blank" rel="noreferrer">
                Open {oauthProviderLabel} authorization page
              </a>
            </p>
          )}
          <div className="form-group">
            <label>{oauthProviderLabel} Callback URL:</label>
            <input
              type="text"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="Paste full redirected URL containing code=..."
              disabled={loading}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handlePullTB}
            disabled={loading || !redirectUrl.trim()}
          >
            {loading ? 'Retrying Pull...' : 'Retry Pull with Callback URL'}
          </button>
        </div>
      )}

      {result && result.success && (
        <div className="alert alert-success">
          <h3>{isManualUploadEntity ? 'Successfully Uploaded TB' : 'Successfully Pulled TB'}</h3>
          <div className="result-details">
            <p><strong>Entity:</strong> {result.entity_name}</p>
            <p><strong>Total Accounts:</strong> {result.summary.total_accounts}</p>
            <p><strong>Total Debit:</strong> {result.summary.total_debit?.toLocaleString()}</p>
            <p><strong>Total Credit:</strong> {result.summary.total_credit?.toLocaleString()}</p>
            <p><strong>Difference:</strong> {result.summary.difference?.toLocaleString()}</p>
            {result.storage && result.storage.stored && (
              <p className="s3-info"><strong>Stored in S3:</strong> {result.storage.timestamp}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PullTBPanel;
